import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'gatsby'
import { language } from '../../types'
import { localizedPath, localize } from '../../utils/language'
import { trackTakeoutOrderClick } from '../TakeoutOrderButton'

const languages: { code: language; label: string; flag: string }[] = [
  { code: 'JA', label: '日本語', flag: 'fi fi-jp' },
  { code: 'EN', label: 'English', flag: 'fi fi-us' },
  { code: 'ZH', label: '中文', flag: 'fi fi-cn' },
  { code: 'TW', label: '繁體中文', flag: 'fi fi-tw' },
  { code: 'KO', label: '한국어', flag: 'fi fi-kr' },
]

const DeskMenu = ({
  isRoot,
  headers,
  lang,
  changeLangLink,
  showFlags,
}: {
  isRoot: boolean,
  headers: any[],
  lang: language,
  changeLangLink: (toLang: language) => string,
  path: string,
  showFlags: boolean
}) => {
  const [langOpen, setLangOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLangOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setLangOpen(false)
    }
  }

  const currentLang = languages.find(l => l.code === lang)!

  return (
    <div className='hidden lg:flex flex-1 items-center min-w-0 gap-3 xl:gap-5 2xl:gap-8'>
      <ul
        className={`flex flex-1 flex-nowrap items-center justify-center whitespace-nowrap text-[15px] leading-none xl:text-lg 2xl:text-xl ${isRoot && 'text-white'} ${lang === 'JA' ? 'gap-5 xl:gap-8 2xl:gap-10' : 'gap-5 xl:gap-9 2xl:gap-12'}`}
      >
        {headers.map(i => (
          <li key={localize(i.title, lang)} className='hover-underline-animation shrink-0'>
            {i.external ? (
              <a
                href={i.link}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() => trackTakeoutOrderClick('header', i.link)}
              >
                {localize(i.title, lang)}
              </a>
            ) : (
              <Link to={localizedPath(lang, i.link)}>
                {localize(i.title, lang)}
              </Link>
            )}
          </li>
        ))}
        {lang === 'JA' && (
          <li className='hover-underline-animation shrink-0'>
            <Link to='/careers/'>採用情報</Link>
          </li>
        )}
      </ul>

      {showFlags && (
        <div
          ref={containerRef}
          className={`relative ml-auto shrink-0 whitespace-nowrap text-[15px] leading-none xl:text-lg 2xl:text-xl ${isRoot && 'text-white'} group`}
          onMouseEnter={() => setLangOpen(true)}
          onMouseLeave={() => setLangOpen(false)}
          onClick={() => setLangOpen(v => !v)}
          onBlur={handleBlur}
        >
          <button
            ref={triggerRef}
            type='button'
            aria-haspopup='menu'
            aria-expanded={langOpen}
            className='flex gap-2 items-center cursor-pointer whitespace-nowrap focus:outline-none'
          >
            <span className={`${currentLang.flag} text-xl`} />
            <span>{currentLang.label}</span>
          </button>

          <div
            className={`
    absolute left-1/2 -translate-x-1/2 top-full z-50 min-w-[120px]
    pt-2
    ${langOpen ? 'block' : 'hidden'}
    group-hover:block focus-within:block
  `}
          >

            <div className='pointer-events-auto absolute -top-2 left-0 right-0 h-2'></div>

            <ul
              role='menu'
              aria-label='Language'
              className='bg-white text-black border rounded shadow-lg overflow-hidden'
            >
              {languages
                .filter(l => l.code !== lang)
                .map(l => (
                  <li role='none' key={l.code}>
                    <Link
                      to={changeLangLink(l.code)}
                      role='menuitem'
                      className='flex gap-2 items-center px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none'
                      onClick={() => setLangOpen(false)}
                    >
                      <span className={`${l.flag} text-xl`} />
                      <span>{l.label}</span>
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default DeskMenu
